import { RouteComponentProps } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet';
import { getPageStatus, PageLayout } from 'components/PageLayout';
import { Hazard } from 'features/hazards/hazard.types';
import { useAuthState } from 'state/useAuthState';
import {
  useHazardGeoEvent,
  hazardDataPatchConfigs,
} from 'features/hazards/hooks/useHazardGeoEvent';
import { ReportForm } from 'features/reports/components/ReportForm';

type ContentProps = {
  geoEvent: Hazard;
};

const Content = (props: ContentProps): JSX.Element => {
  const { geoEvent } = props;
  const { t } = useTranslation();

  const getViewPath = (geoEventId: number): string => {
    return `/i/${geoEventId}`;
  };

  return (
    <>
      <Helmet>
        <title>
          {t('addIncidentReport.titleWithName', { name: geoEvent.name })}
        </title>
      </Helmet>
      <ReportForm
        geoEvent={geoEvent}
        geoEventPatchConfigs={{
          dataPatchConfigs: hazardDataPatchConfigs,
        }}
        getViewPath={getViewPath}
      />
    </>
  );
};

export const AddHazardReport = (
  props: RouteComponentProps<{ geoEventId: string }>,
): JSX.Element => {
  const {
    match: {
      params: { geoEventId },
    },
  } = props;
  const { t } = useTranslation();
  const {
    permissions: { canReportHazard },
  } = useAuthState();

  const { geoEvent, isGeoEventLoading, isGeoEventFetchError } =
    useHazardGeoEvent({
      id: Number(geoEventId),
    });

  const status = getPageStatus({
    isUnauthorized:
      !canReportHazard || (geoEvent && geoEvent.geoEventType !== 'hazard'),
    isLoading: isGeoEventLoading,
    isError: isGeoEventFetchError || !geoEvent,
  });

  return (
    <PageLayout
      data-cname="AddHazardReportPage"
      status={status}
      headerBarProps={{
        title: geoEvent
          ? t('addIncidentReport.titleWithName', {
              name: geoEvent.name,
            })
          : undefined,
        action: 'back',
      }}
    >
      {geoEvent && <Content geoEvent={geoEvent} />}
    </PageLayout>
  );
};
